import type { ColumnConfig, TableRowData } from '@/components/DataTable';
import type {
  ToolApproval,
  ToolApprovalScope,
} from '@kirin/api-client/contracts/shadow-ai-tool-approvals';
import type { OrgWriteGate } from '@/hooks/useRoleCheck';
import { ToolApprovalActionCell, ToolApprovalStatus } from './ToolApprovalCell';

/** Column id — also the key the Approval column is addressed by in tests. */
export const TOOL_APPROVAL_COLUMN_ID = 'approval';

type ApprovalRow = TableRowData & { approval?: ToolApproval | null };

export interface ToolApprovalColumnOptions<TRow extends ApprovalRow> {
  /** Id the approve/unapprove endpoints key on (Browser: domain; else toolId). */
  getToolId: (row: TRow) => string;
  scope: ToolApprovalScope;
  /**
   * Org surfaces. With it, the write gate picks the presentation:
   * owner/manager → working controls, support → disabled controls, member (or
   * still loading) → read-only.
   */
  organizationId?: string;
  writeGate?: OrgWriteGate;
  /**
   * KIR-4864: personal views — working controls that write the caller's own
   * personal approvals (only the account owner sees these tabs).
   */
  personal?: boolean;
}

/**
 * KIR-4854: the trailing "Approval" column shared by the Browser AI Tools,
 * Installed AI Tools and Running Now tables (org + personal).
 */
export function createToolApprovalColumn<TRow extends ApprovalRow>({
  getToolId,
  scope,
  organizationId,
  writeGate,
  personal = false,
}: ToolApprovalColumnOptions<TRow>): ColumnConfig<TRow> {
  let controls: { organizationId: string | null; disabled: boolean } | null =
    null;
  if (personal) {
    controls = { organizationId: null, disabled: false };
  } else if (
    organizationId &&
    writeGate &&
    !writeGate.hidden &&
    !writeGate.isLoading
  ) {
    controls = { organizationId, disabled: writeGate.disabled };
  }

  return {
    id: TOOL_APPROVAL_COLUMN_ID,
    header: 'Approval',
    // Action buttons must stay fully visible.
    clipOverflow: false,
    accessor: row => row.approval ?? null,
    width: 260,
    sortable: false,
    renderCell: ({ row }) =>
      controls ? (
        <ToolApprovalActionCell
          organizationId={controls.organizationId}
          scope={scope}
          toolId={getToolId(row)}
          approval={row.approval}
          disabled={controls.disabled}
        />
      ) : (
        <ToolApprovalStatus approval={row.approval} />
      ),
  };
}
